import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { desktopShell, isDesktopMenuCommand } from "@/lib/desktopBridge";
import { useDesktopActions } from "@/desktop/useDesktopActions";
import { restoreDesktopTabs } from "@/desktop/tabs";

/**
 * The renderer end of the shell (TODO §14.7.4, §14.7.6). Renders nothing.
 *
 * Two jobs: replay the previously open tabs on launch, and turn native menu commands into the
 * same actions the in-app buttons run. Mounted once, inside the router — everything it does
 * needs `navigate`, and the renderer staying the source of truth is the whole design.
 */
export function DesktopShellBridge() {
  const navigate = useNavigate();
  const actions = useDesktopActions();

  // §14.7.6 Restore the tab strip, and show the deck that was in front. Only from the start
  // screen: a window opened straight onto a deck (§14.7.2's second window, or a reload of
  // `/d/:id`) already knows what it is showing and must not be navigated away from it.
  useEffect(() => {
    void restoreDesktopTabs().then((deckId) => {
      if (deckId && window.location.pathname === "/") {
        navigate(`/d/${deckId}`, { replace: true });
      }
    });
  }, [navigate]);

  useEffect(() => {
    const shell = desktopShell();
    if (!shell) return;
    return shell.onMenuCommand((command) => {
      if (!isDesktopMenuCommand(command)) return;
      switch (command) {
        case "new-deck":
          void actions.newDeck();
          break;
        case "open-deck":
          void actions.openDeckFile();
          break;
        case "save-a-copy":
          void actions.saveACopy();
          break;
        case "close-tab":
          actions.closeTab();
          break;
        case "next-tab":
          actions.cycleTab(1);
          break;
        case "previous-tab":
          actions.cycleTab(-1);
          break;
        case "open-in-new-window":
          actions.openInNewWindow();
          break;
        case "documents":
          actions.showDocuments();
          break;
      }
    });
  }, [actions]);

  return null;
}
